import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SpaceBackground from '../components/SpaceBackground';
import Marquee from 'react-fast-marquee';
import useEmblaCarousel from 'embla-carousel-react';
import { getFeaturedProjects } from '../data/projects';
import {
  Rocket,
  Target,
  Users,
  Zap,
  TrendingUp,
  Globe,
  Shield,
  Cpu,
  DollarSign,
  Leaf,
  Database,
  Heart,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Lightbulb,
  BarChart,
  Building,
  Briefcase,
  Award,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Link2,
  Server,
  Menu,
  X
} from 'lucide-react';

const Home = () => {
  const [selectedJourneyStep, setSelectedJourneyStep] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  
  // Get featured projects from data
  const featuredProjects = getFeaturedProjects();
  
  // Embla carousel setup - rotate one card at a time through 1,2,3,4 sequence
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true, 
    align: 'start',
    slidesToScroll: 1
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Handle scroll state for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Determine active section
      const sections = ['about', 'how-we-work', 'industries', 'projects'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Journey steps data
  const journeySteps = [
    {
      id: 'discover',
      number: '01',
      title: 'Discover & Align',
      description: 'Clarify goals, constraints, and where execution creates leverage.',
      imageSrc: '/journey-step1.png',
      altText: 'Strategy and discovery visualization with interconnected nodes',
      icon: <Target className="icon" />
    },
    {
      id: 'prototype',
      number: '02',
      title: 'Co-design & Prototype',
      description: 'Build pilots, roadmaps, and early wins with real-world fit.',
      imageSrc: '/journey-step2.png',
      altText: 'Prototyping visualization with wireframe elements',
      icon: <Lightbulb className="icon" />
    },
    {
      id: 'execute',
      number: '03',
      title: 'Execute & Scale',
      description: 'Deliver with speed and precision using embedded local teams.',
      imageSrc: '/journey-step3.png',
      altText: 'Execution visualization with upward growth trajectory',
      icon: <TrendingUp className="icon" />
    },
    {
      id: 'optimize',
      number: '04',
      title: 'Optimise & Evolve',
      description: 'Stay engaged to refine performance and extend long-term impact.',
      imageSrc: '/journey-step4.png',
      altText: 'Optimization visualization with analytics dashboard',
      icon: <BarChart className="icon" />
    }
  ];

  return (
    <div className="landing-page">
      {/* Fixed Background */}
      <SpaceBackground />

      {/* Scrollable Content */}
      <div className="page-content">
        {/* Navbar - Sticky within page content */}
        <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="navbar">
          <div className="header-content">
            <div className="logo-container">
              <img 
                src="/moonrock-logo-light.png" 
                alt="MoonRock Research & Development" 
                className="navbar-logo-image"
              />
            </div>
            
            {/* Desktop Nav */}
            <nav className="nav-menu" data-testid="nav-menu">
              <Link 
                to="/who-we-are"
                className="nav-link-page"
              >
                Who We Are
              </Link>
              <a 
                href="#how-we-work"
                className={activeSection === 'how-we-work' ? 'active' : ''}
              >
                How We Work
              </a>
              <a 
                href="#industries"
                className={activeSection === 'industries' ? 'active' : ''}
              >
                Focus
              </a>
              <Link 
                to="/projects"
                className="nav-link-page"
              >
                Projects
              </Link>
            </nav>

            {/* CTA Button */}
            <div className="nav-cta-wrapper">
              <Link to="/contact">
                <Button className="nav-cta" data-testid="nav-cta">Partner With Us</Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
            <nav className="mobile-nav-links">
              <Link 
                to="/who-we-are"
                className="nav-link-page"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Who We Are
              </Link>
              <a 
                href="#how-we-work"
                className={activeSection === 'how-we-work' ? 'active' : ''}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                How We Work
              </a>
              <a 
                href="#industries"
                className={activeSection === 'industries' ? 'active' : ''}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Focus
              </a>
              <Link 
                to="/projects"
                className="nav-link-page"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Projects
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="mobile-nav-cta">Partner With Us</Button>
              </Link>
            </nav>
          </div>
        </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-atmosphere"></div>
        <div className="hero-content">
          <span className="hero-label">Venture Studio & Tactical Incubator</span>
          <h1 className="hero-title">We Shape What Lasts.</h1>
          <p className="hero-subtitle">
            Moonrock R&D partners with founders, enterprises, and government teams to take ideas from discovery to execution—building real ventures in Oman with measurable regional impact.
          </p>
          <div className="hero-pillars">
            <span className="pillar-chip">AI</span>
            <span className="pillar-divider">•</span>
            <span className="pillar-chip">DATA CENTERS</span>
            <span className="pillar-divider">•</span>
            <span className="pillar-chip">CLEANTECH</span>
            <span className="pillar-divider">•</span>
            <span className="pillar-chip">ENERGY INFRASTRUCTURE</span>
            <span className="pillar-divider">•</span>
            <span className="pillar-chip">FINTECH</span>
          </div>
          <div className="hero-buttons">
            <Link to="/projects">
              <Button size="lg" className="hero-btn-primary" data-testid="hero-cta-primary">
                Explore Our Ventures
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="hero-btn-secondary" data-testid="hero-cta-secondary">
                Partner With Us
              </Button>
            </Link>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })} data-testid="scroll-indicator">
          <span className="scroll-indicator-text">Scroll</span>
          <div className="scroll-indicator-icon">
            <div className="scroll-indicator-dot"></div>
          </div>
        </div>
      </section>

      {/* Section Divider - Subtle line */}
      <div className="section-divider-line"></div>

      {/* How We Work - Redesigned */}
      <section id="how-we-work" className="how-we-work-section-v2">
        <div className="hww-atmosphere"></div>
        <div className="hww-container">
          {/* Section Header */}
          <div className="hww-header">
            <span className="hww-eyebrow">How We Work</span>
            <h2 className="hww-title">An Execution Model You Can Measure.</h2>
            <p className="hww-subtitle">
              We don't operate from the sidelines. We embed with founders, corporates, and government partners to deliver outcomes you can measure—fast, responsibly, and with long-term ownership in mind.
            </p>
          </div>

          {/* Process Flow - Horizontal Timeline */}
          <div className="hww-process-flow">
            <div className="hww-process-line"></div>
            <div className="hww-process-steps">
              <div className="hww-process-step">
                <div className="hww-step-dot">
                  <span className="hww-step-number">01</span>
                </div>
                <div className="hww-step-content">
                  <h3 className="hww-step-title">Embed</h3>
                  <p className="hww-step-desc">Teams working inside the venture, not outside the room.</p>
                </div>
              </div>
              <div className="hww-process-step">
                <div className="hww-step-dot">
                  <span className="hww-step-number">02</span>
                </div>
                <div className="hww-step-content">
                  <h3 className="hww-step-title">Validate</h3>
                  <p className="hww-step-desc">Proof-of-concepts and pilots that reduce risk early.</p>
                </div>
              </div>
              <div className="hww-process-step">
                <div className="hww-step-dot">
                  <span className="hww-step-number">03</span>
                </div>
                <div className="hww-step-content">
                  <h3 className="hww-step-title">Scale</h3>
                  <p className="hww-step-desc">KPIs defined up front, impact tracked from day one.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider-line"></div>

      {/* Who We Build With - Equal Cards Grid */}
      <section id="partners" className="partners-section-v2">
        <div className="partners-v2-content">
          <div className="partners-v2-header">
            <span className="partners-v2-eyebrow">Strategic Partners</span>
            <h2 className="partners-v2-title">Who we build with.</h2>
            <p className="partners-v2-subtitle">We partner with visionaries across sectors to turn ambition into measurable impact.</p>
          </div>
          
          <div className="partners-v2-grid">
            {/* Omani B2B */}
            <div className="partner-card-v2">
              <div className="partner-card-icon">
                <Briefcase size={28} />
              </div>
              <h3 className="partner-card-title">Omani B2B Innovators</h3>
              <p className="partner-card-desc">Pioneering local solutions with global ambition.</p>
            </div>
            
            {/* Government */}
            <div className="partner-card-v2">
              <div className="partner-card-icon">
                <Shield size={28} />
              </div>
              <h3 className="partner-card-title">Government Partners</h3>
              <p className="partner-card-desc">Strategic alliances for national digitisation.</p>
            </div>
            
            {/* Global */}
            <div className="partner-card-v2">
              <div className="partner-card-icon">
                <Globe size={28} />
              </div>
              <h3 className="partner-card-title">Global Collaborators</h3>
              <p className="partner-card-desc">Bridging global ambition with local execution.</p>
            </div>
            
            {/* Investors */}
            <div className="partner-card-v2">
              <div className="partner-card-icon">
                <TrendingUp size={28} />
              </div>
              <h3 className="partner-card-title">Investors</h3>
              <p className="partner-card-desc">Supporting high-impact ventures with traction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider-line"></div>

      {/* Focus Areas - Redesigned with 4 Sectors */}
      <section id="industries" className="industries-premium">
        <div className="industries-premium-container">
          <div className="industries-premium-header">
            <span className="industries-premium-label">Focus Areas</span>
            <h2 className="industries-premium-title">Our Focus Areas</h2>
            <p className="industries-premium-desc">
              We build and scale ventures across four priority sectors.
            </p>
          </div>
          
          <div className="industries-premium-grid">
            {/* Intelligent Digital Systems */}
            <div className="industry-premium-card">
              <div className="industry-premium-bg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/52aedb38-24ea-4ee4-8eda-846c0d3c3b94/images/de802dab00a17be8cd414d8cd330c8312522168752614f028867ea81b0c336ee.png" 
                  alt="Intelligent Digital Systems"
                />
                <div className="industry-premium-gradient"></div>
              </div>
              <div className="industry-premium-body">
                <div className="industry-premium-icon">
                  <Cpu size={22} />
                </div>
                <h3 className="industry-premium-name">Intelligent Digital Systems</h3>
                <p className="industry-premium-text">AI-driven platforms, automation, and data-led solutions that modernise operations.</p>
              </div>
            </div>
            
            {/* Next-Generation Financial Platforms */}
            <div className="industry-premium-card">
              <div className="industry-premium-bg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/52aedb38-24ea-4ee4-8eda-846c0d3c3b94/images/67ba6d46090ea907198b2a9ec1a53017ae1509edfe43a9f1da14c996af44ddd7.png" 
                  alt="Next-Generation Financial Platforms"
                />
                <div className="industry-premium-gradient"></div>
              </div>
              <div className="industry-premium-body">
                <div className="industry-premium-icon">
                  <DollarSign size={22} />
                </div>
                <h3 className="industry-premium-name">Next-Gen Financial Platforms</h3>
                <p className="industry-premium-text">Secure FinTech and blockchain infrastructure for trusted transactions.</p>
              </div>
            </div>
            
            {/* Sustainable Energy & Climate Solutions */}
            <div className="industry-premium-card">
              <div className="industry-premium-bg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/52aedb38-24ea-4ee4-8eda-846c0d3c3b94/images/2aa94d4a75c1d6ab4c56ccebe0414b41b0df830a2a5323d4fab9f38ab2d6286a.png" 
                  alt="Sustainable Energy & Climate Solutions"
                />
                <div className="industry-premium-gradient"></div>
              </div>
              <div className="industry-premium-body">
                <div className="industry-premium-icon">
                  <Leaf size={22} />
                </div>
                <h3 className="industry-premium-name">Sustainable Energy & Climate</h3>
                <p className="industry-premium-text">Renewables and energy optimisation for long-term sustainability impact.</p>
              </div>
            </div>
            
            {/* Critical Industrial Infrastructure */}
            <div className="industry-premium-card">
              <div className="industry-premium-bg">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/52aedb38-24ea-4ee4-8eda-846c0d3c3b94/images/7f01f3dac6ed84d1a71e016e121ba9c860426879b90eb050d00135e12fc9e79d.png" 
                  alt="Critical Industrial Infrastructure"
                />
                <div className="industry-premium-gradient"></div>
              </div>
              <div className="industry-premium-body">
                <div className="industry-premium-icon">
                  <Server size={22} />
                </div>
                <h3 className="industry-premium-name">Critical Industrial Infrastructure</h3>
                <p className="industry-premium-text">Data centres and industrial platforms built for national-scale execution.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider-line"></div>

      {/* Featured Projects - Redesigned Carousel */}
      <section id="projects" className="projects-section-v2">
        <div className="projects-v2-container">
          <div className="projects-v2-header">
            <span className="projects-v2-eyebrow">Our Projects</span>
            <h2 className="projects-v2-title">What We're Building</h2>
            <p className="projects-v2-subtitle">Selected ventures and partnerships in execution today.</p>
          </div>
          
          {/* Carousel */}
          <div className="projects-v2-carousel-wrapper">
            <button 
              className="projects-v2-nav projects-v2-nav-prev" 
              onClick={scrollPrev}
              aria-label="Previous project"
              data-testid="carousel-prev"
            >
              <ChevronLeft size={24} />
            </button>
            
            <div className="projects-v2-carousel" ref={emblaRef}>
              <div className="projects-v2-carousel-track">
                {featuredProjects.map((project, index) => (
                  <div key={project.id} className="projects-v2-slide" data-testid={`project-slide-${index}`}>
                    <div className="projects-v2-card">
                      <div className="projects-v2-card-image">
                        {project.imageSrc ? (
                          <img src={project.imageSrc} alt={project.name} />
                        ) : (
                          <div className="projects-v2-placeholder">
                            <span className="projects-v2-placeholder-text">
                              {project.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                        )}
                        <div className="projects-v2-card-gradient"></div>
                      </div>
                      <div className="projects-v2-card-content">
                        <h3 className="projects-v2-card-name">{project.name}</h3>
                        <p className="projects-v2-card-teaser">{project.teaser}</p>
                        <Link to={`/projects/${project.slug}`} className="projects-v2-card-cta">
                          View Project
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button 
              className="projects-v2-nav projects-v2-nav-next" 
              onClick={scrollNext}
              aria-label="Next project"
              data-testid="carousel-next"
            >
              <ChevronRight size={24} />
            </button>
          </div>
          
          {/* Dots Indicator */}
          <div className="projects-v2-dots">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={`projects-v2-dot ${index === selectedIndex ? 'active' : ''}`}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          
          {/* View All Projects Button */}
          <div className="projects-v2-cta">
            <Link to="/projects">
              <Button size="lg" className="projects-v2-btn" data-testid="view-all-projects-btn">
                View All Projects
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="section-divider-line"></div>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-glass">
          <div className="footer-content">
            <div className="footer-grid">
              {/* Brand Block */}
              <div className="footer-brand">
                <div className="footer-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/8gbuyg09_Full%20Logo%20Light%20TP%20space%20%281%29.png" 
                    alt="MoonRock R&D" 
                    style={{ height: '56px', width: 'auto', marginBottom: '14px', objectFit: 'contain' }}
                  />
                  <p className="footer-tagline" style={{ margin: 0, paddingLeft: '10px' }}>We Shape What Lasts.</p>
                </div>
              </div>
              
              {/* Quick Links */}
              <div className="footer-links">
                <h4 className="footer-heading">Quick Links</h4>
                <ul>
                  <li><Link to="/who-we-are">Who We Are</Link></li>
                  <li><a href="#how-we-work">How We Work</a></li>
                  <li><a href="#industries">Focus</a></li>
                  <li><Link to="/projects">Projects</Link></li>
                </ul>
              </div>
              
              {/* Contact */}
              <div className="footer-contact">
                <h4 className="footer-heading">Contact</h4>
                <div className="contact-info">
                  <div className="contact-item">
                    <MapPin className="contact-icon" />
                    <span>Suite 403, Bldg. 393, 18th November St, Muscat</span>
                  </div>
                  <div className="contact-item">
                    <Mail className="contact-icon" />
                    <a href="mailto:admin@moonrock.om">admin@moonrock.om</a>
                  </div>
                  <div className="contact-item">
                    <Phone className="contact-icon" />
                    <a href="tel:+96896607256">+968 9660 7256</a>
                  </div>
                  <div className="contact-item">
                    <Globe className="contact-icon" />
                    <a href="https://moonrock.om" target="_blank" rel="noopener noreferrer">moonrock.om</a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bottom Row */}
            <div className="footer-divider"></div>
            <div className="footer-bottom">
              <p className="footer-copyright">© 2026 MoonRock R&D. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default Home;
